// UnifiedActivityCard.jsx — v3
// COLOR SCHEME:
//   incoming BBC split → yellow-leaning chartreuse (#8C7A0A, leans amber not green)
//   bbc-split-out      → amber      (#CA8A04)
//   incoming invoice   → green      (#16A34A)
//   blitzpay           → red        (#DC2626)
//   transfer/withdraw  → blue       (#2563EB)
//
// LAYOUT: Uniform card height via fixed-height section slots.
//   Every section always renders (with empty fallback) so cards are same size.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardContent, CardActions, Box, Typography, Button, Chip,
  Stack, Collapse, Avatar, IconButton, Tooltip, Alert, Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon, Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon, SwapHoriz as SwapHorizIcon,
  Person as PersonIcon, Group as GroupIcon, Business as BusinessIcon,
  Visibility as VisibilityIcon, Edit as EditIcon, CheckCircle as CheckCircleIcon,
  Speed as SpeedIcon, Schedule as ScheduleIcon, Warning as WarningIcon,
  Info as InfoIcon, OpenInNew as OpenInNewIcon, Assignment as AssignmentIcon,
  Add as AddIcon, AccountBalance as AccountBalanceIcon, ArrowUpward as ArrowUpIcon,
  Bolt as BoltIcon, Settings as SettingsIcon, CallSplit as CallSplitIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

// ── Yellow-leaning chartreuse — BBC Split INCOMING ──────────────────────── //
// Shifted 2/3 toward amber on hue arc vs green: warm yellow that winks at green
const YG        = "#8C7A0A";
const YG_LIGHT  = "#F0DC68";
const YG_BG     = "#FEFBE8";
const YG_DARK   = "#6E6008";
const YG_DEEP   = "#504505";
const YG_INK    = "#1A1400";
const YG_TINT   = alpha("#8C7A0A", 0.07);
const YG_BORDER = alpha("#8C7A0A", 0.35);

// ── Amber — BBC Split OUTGOING ──────────────────────────────────────────── //
const Y        = "#CA8A04";
const Y_LIGHT  = "#FDE68A";
const Y_DARK   = "#A16207";
const Y_DEEP   = "#78350F";
const Y_INK    = "#1C0A00";
const Y_TINT   = alpha("#CA8A04", 0.07);
const Y_BORDER = alpha("#CA8A04", 0.35);

// ── Green — Incoming invoices ────────────────────────────────────────────── //
const G        = "#16A34A";
const G_DARK   = "#15803D";
const G_DEEP   = "#14532D";
const G_INK    = "#FFFFFF";
const G_TINT   = alpha("#16A34A", 0.07);
const G_BORDER = alpha("#16A34A", 0.30);

// ── Red — BlitzPay ──────────────────────────────────────────────────────── //
const R        = "#DC2626";
const R_DARK   = "#B91C1C";
const R_DEEP   = "#7F1D1D";
const R_INK    = "#FFFFFF";
const R_TINT   = alpha("#DC2626", 0.07);
const R_BORDER = alpha("#DC2626", 0.28);

// ── Blue — Transfers ────────────────────────────────────────────────────── //
const B        = "#2563EB";
const B_DARK   = "#1D4ED8";
const B_DEEP   = "#1E3A8A";
const B_INK    = "#FFFFFF";
const B_TINT   = alpha("#2563EB", 0.07);
const B_BORDER = alpha("#2563EB", 0.28);

const currency = new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" });

// ─────────────────────────────────────────────────────────────────────────────
// PURE HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const isBBCSplit = (item) =>
  item?.invoice_type === "bbc" || item?.source_type === "bbc" || item?.isBBC;

const categorizeItem = (item) => {
  if (item.isIncoming && isBBCSplit(item))  return "bbc-split-incoming";
  if (!item.isIncoming && isBBCSplit(item)) return "bbc-split-outgoing";
  if (item.recordType === "transfer" || item.type === "Internal Transfer" || item.transaction_type === "internal_transfer") return "transfer";
  if (item.recordType === "withdrawal" || item.type === "Withdrawal") return "withdrawal";
  if (item.type === "Payout" || item.recordType === "payout") return "payout";
  if (item.isIncoming || item.invoice_type === "creator") return "invoice-incoming";
  if (item.isBlitzPay || item.recordType === "blitzpay-invoice" || item.invoice_type === "blitzpay" || item.source_type === "blitzpay") return "blitzpay";
  return "invoice-outgoing";
};

// Static config per type — avoids re-creating objects in render
const TYPE_CONFIG = {
  "bbc-split-incoming": {
    label: "Split Received", color: "warning",
    mkIcon: () => <CallSplitIcon sx={{ fontSize: 20, color: YG_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${YG_TINT} 0%, ${alpha(YG, 0.04)} 100%)`,
    accent: { main: YG, light: YG_LIGHT, bg: YG_BG, dark: YG_DARK, deep: YG_DEEP, ink: YG_INK, tint: YG_TINT, border: YG_BORDER },
    pillBg: YG_LIGHT, pillInk: YG_INK,
  },
  "bbc-split-outgoing": {
    label: "Split Sent", color: "warning",
    mkIcon: () => <BoltIcon sx={{ fontSize: 20, color: Y_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${Y_TINT} 0%, ${alpha(Y, 0.04)} 100%)`,
    accent: { main: Y, light: Y_LIGHT, dark: Y_DARK, deep: Y_DEEP, ink: Y_INK, tint: Y_TINT, border: Y_BORDER },
    pillBg: Y_LIGHT, pillInk: Y_INK,
  },
  "transfer": {
    label: "Transfer", color: "info",
    mkIcon: () => <SwapHorizIcon sx={{ fontSize: 20, color: B_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${B_TINT} 0%, ${alpha(B, 0.04)} 100%)`,
    accent: { main: B, dark: B_DARK, deep: B_DEEP, ink: B_INK, tint: B_TINT, border: B_BORDER },
    pillBg: B, pillInk: B_INK,
  },
  "withdrawal": {
    label: "Withdrawal", color: "info",
    mkIcon: () => <ArrowUpIcon sx={{ fontSize: 20, color: B_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${B_TINT} 0%, ${alpha(B, 0.04)} 100%)`,
    accent: { main: B, dark: B_DARK, deep: B_DEEP, ink: B_INK, tint: B_TINT, border: B_BORDER },
    pillBg: B, pillInk: B_INK,
  },
  "payout": {
    label: "Payout", color: "info",
    mkIcon: () => <PaymentIcon sx={{ fontSize: 20, color: B_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${B_TINT} 0%, ${alpha(B, 0.04)} 100%)`,
    accent: { main: B, dark: B_DARK, deep: B_DEEP, ink: B_INK, tint: B_TINT, border: B_BORDER },
    pillBg: B, pillInk: B_INK,
  },
  "blitzpay": {
    label: "BlitzPay", color: "error",
    mkIcon: () => <SwapHorizIcon sx={{ fontSize: 20, color: R_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${R_TINT} 0%, ${alpha(R, 0.04)} 100%)`,
    accent: { main: R, dark: R_DARK, deep: R_DEEP, ink: R_INK, tint: R_TINT, border: R_BORDER },
    pillBg: R, pillInk: R_INK,
  },
  "invoice-incoming": {
    label: "Incoming", color: "success",
    mkIcon: () => <TrendingUpIcon sx={{ fontSize: 20, color: G_INK }} />,
    bgGradient: () => `linear-gradient(135deg, ${G_TINT} 0%, ${alpha(G, 0.04)} 100%)`,
    accent: { main: G, dark: G_DARK, deep: G_DEEP, ink: G_INK, tint: G_TINT, border: G_BORDER },
    pillBg: G, pillInk: G_INK,
  },
  "invoice-outgoing": {
    label: "Outgoing", color: "secondary",
    mkIcon: () => <ReceiptIcon sx={{ fontSize: 20 }} />,
    bgGradient: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.light, 0.04)} 100%)`,
    accent: null, pillBg: null, pillInk: null,
  },
};
const getTypeConfig = (itemType) => TYPE_CONFIG[itemType] || {
  label: "Unknown", color: "default",
  mkIcon: () => <ReceiptIcon sx={{ fontSize: 20 }} />,
  bgGradient: (t) => alpha(t.palette.grey[500], 0.04),
  accent: null, pillBg: null, pillInk: null,
};

const getEntityInfo = (item, itemType) => {
  if (itemType === "bbc-split-incoming") {
    const senderName = item.client_name || item.company_name || item.name || "Agency";
    const recipientCount = item.creator_payments?.length || item.recipient_count || item.custom_details?.recipient_count || null;
    const preCount = item.pre_accepted_count || (item.creator_payments || []).filter((p) => p.preapproved || p.blitzpay_preapproved).length || 0;
    return { name: senderName, subtitle: recipientCount ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""}${preCount > 0 ? ` · ${preCount} pre-accepted` : ""}` : "Blitz Split invoice" };
  }
  if (itemType === "bbc-split-outgoing") {
    const clientName = item.client_name || item.name || "Enterprise Client";
    const recipientCount = item.recipient_count || item.custom_details?.recipient_count || null;
    const s = (item.status || "").toLowerCase();
    const statusLabel = s === "approved" ? "Approved — awaiting funding" : (s === "paid" || s === "funded") ? "Funded" : "Awaiting client approval";
    return { name: clientName, subtitle: recipientCount ? `${recipientCount} contractor${recipientCount !== 1 ? "s" : ""} · ${statusLabel}` : statusLabel };
  }
  if (itemType === "transfer")   return { name: item.name || item.brief || "Balance Transfer", subtitle: item.description || "Internal transfer" };
  if (itemType === "withdrawal") return { name: item.name || `Withdrawal to ${item.payment_service || "Bank"}`, subtitle: item.description || item.notes || "Bank transfer" };
  if (itemType === "payout") {
    const ci = item.creator_info || {};
    const id = item.creator_id || ci.creator_id;
    return { name: ci.display_name || item.creator_display_name || item.name || id || "Creator", subtitle: id ? `@${id}` : null };
  }
  if (itemType === "blitzpay") {
    const isMulti = item.isMultiRecipient && item.recipientCount > 1;
    if (isMulti) {
      const payouts = item.payoutDetailsArray || [];
      const names = payouts.map((pd) => pd.name || pd.creator_display_name || pd.creator_id || null).filter(Boolean);
      const MAX = 2;
      const displayName = names.length === 0 ? (item.campaign_name || "BlitzPay to Multiple Creators") : names.length <= MAX ? `BlitzPay to ${names.join(", ")}` : `BlitzPay to ${names.slice(0, MAX).join(", ")} +${names.length - MAX} more`;
      return { name: displayName, subtitle: `${item.recipientCount} creators` };
    }
    const displayName = item.campaign_name || item.name || "BlitzPay Payment";
    if (item.payoutDetails) {
      const creatorId = item.payoutDetails.creator_id;
      const creatorName = item.payoutDetails.creator_info?.display_name || creatorId;
      return { name: displayName, subtitle: creatorName ? `@${creatorName}` : "Creator payment" };
    }
    const creatorId = item.creator_id;
    const isBatch = !creatorId || displayName?.toLowerCase().includes("batch") || displayName?.toLowerCase().includes("multiple");
    return { name: displayName, subtitle: isBatch ? "Multiple creators" : creatorId ? `@${creatorId}` : "Creator payment" };
  }
  if (itemType === "invoice-incoming") {
    const senderName = item.client_name || item.name || item.creator_id;
    return { name: senderName || "Vendor", subtitle: item.creator_id ? `@${item.creator_id}` : null };
  }
  const recipientName = item.client_name || item.name || item.creator_id;
  return { name: recipientName || "Client", subtitle: item.creator_id ? `@${item.creator_id}` : null };
};

const getStatusChip = (status) => {
  const s = (status || "").toLowerCase();
  const cfg = { pending: "warning", approved: "info", completed: "success", paid: "success", funded: "success", cancelled: "error", declined: "error", processing: "warning" };
  return <Chip label={status} size="small" color={cfg[s] || "default"} sx={{ fontWeight: 600, textTransform: "capitalize", fontSize: "0.72rem", height: 22 }} />;
};

const needsApproval = (item, itemType) => {
  if (["transfer", "withdrawal", "bbc-split-outgoing"].includes(itemType)) return false;
  const status = (item.status || item.invoice_status || "").toLowerCase();
  if (itemType === "bbc-split-incoming") return status === "pending";
  return ["invoice-incoming", "blitzpay"].includes(itemType) && status === "pending";
};
const needsManage  = (itemType) => itemType === "bbc-split-outgoing";
const isEditable   = (item, itemType) => itemType === "invoice-outgoing" && !["paid", "completed", "funded"].includes((item.status || "").toLowerCase());
const isViewable   = (item) => ["paid", "completed", "funded", "approved"].includes((item.status || item.invoice_status || "").toLowerCase());

const getInvoiceRoute = (item, itemType) => {
  if (["transfer", "withdrawal", "payout", "bbc-split-incoming", "bbc-split-outgoing"].includes(itemType)) return null;
  const s = (item.status || item.invoice_status || "").toLowerCase();
  const base = item.invoice_type === "creator" || itemType === "invoice-incoming" ? `/creatorinvoice/${item.id}` : `/invoicing/${item.id}`;
  return s === "pending" ? `${base}?needsApproval=true` : base;
};

const getBBCStatusPillProps = (status, isOutgoing) => {
  const s = (status || "").toLowerCase();
  if (isOutgoing) {
    if (s === "pending")                return { label: "Awaiting Approval",   bg: Y_TINT,  fg: Y_DEEP,  border: Y_BORDER  };
    if (s === "approved")               return { label: "Approved · Electing", bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "paid" || s === "funded") return { label: "Funded & Settled",    bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "cancelled")              return { label: "Cancelled",            bg: R_TINT,  fg: R_DEEP,  border: R_BORDER  };
                                        return { label: status,                 bg: Y_TINT,  fg: Y_DEEP,  border: Y_BORDER  };
  } else {
    if (s === "pending")                return { label: "Action Required",     bg: YG_TINT, fg: YG_DEEP, border: YG_BORDER };
    if (s === "approved")               return { label: "Approved · Settling", bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "paid" || s === "funded") return { label: "Funded",              bg: G_TINT,  fg: G_DEEP,  border: G_BORDER  };
    if (s === "cancelled")              return { label: "Cancelled",            bg: R_TINT,  fg: R_DEEP,  border: R_BORDER  };
                                        return { label: status,                 bg: YG_TINT, fg: YG_DEEP, border: YG_BORDER };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const ExternalPOSection = ({ externalPoNumber, erpSystemName, onUpdate }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer", flexWrap: "wrap" }} onClick={() => onUpdate?.()}>
    <AssignmentIcon sx={{ fontSize: 13, color: "text.secondary", flexShrink: 0 }} />
    {externalPoNumber ? (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", fontSize: "0.68rem" }}>{erpSystemName ? `${erpSystemName} PO:` : "External PO:"}</Typography>
        <Chip label={externalPoNumber} size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 600, maxWidth: 100 }} />
        <EditIcon sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }} />
      </>
    ) : (
      <>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.68rem" }}>Add external PO</Typography>
        <AddIcon sx={{ fontSize: 12, color: "primary.main", flexShrink: 0 }} />
      </>
    )}
  </Box>
);

// Consistent 22px pill used for type labels
const TypePill = ({ bg, ink, icon, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, px: 0.875, height: 22, borderRadius: "5px", bgcolor: bg || "grey.300", mb: "3px", maxWidth: "100%", overflow: "hidden" }}>
    {React.cloneElement(icon, { sx: { fontSize: 10, flexShrink: 0, color: ink } })}
    <Typography sx={{ fontWeight: 800, color: ink, fontSize: "0.67rem", lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {label}
    </Typography>
  </Box>
);

// Consistent 22px pill used for BBC status
const StatusPill = ({ label, bg, fg, border }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", px: 0.875, height: 22, borderRadius: "5px", bgcolor: bg, border: `1px solid ${border}`, maxWidth: 160, overflow: "hidden" }}>
    <Typography noWrap sx={{ fontWeight: 700, color: fg, fontSize: "0.67rem", lineHeight: 1 }}>{label}</Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN CARD
// ─────────────────────────────────────────────────────────────────────────────

const UnifiedActivityCard = ({ item, onEdit, onApprove, onView, onViewDetails }) => {
  const theme    = useTheme();
  const navigate = useNavigate();
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);

  const itemType        = categorizeItem(item);
  const typeConfig      = getTypeConfig(itemType);
  const entityInfo      = getEntityInfo(item, itemType);
  const isNeedsApproval = needsApproval(item, itemType);
  const isNeedsManage   = needsManage(itemType);
  const canEdit         = isEditable(item, itemType);
  const completed       = isViewable(item);

  const isIncomingBBC = itemType === "bbc-split-incoming";
  const isOutgoingBBC = itemType === "bbc-split-outgoing";
  const isAnyBBC      = isIncomingBBC || isOutgoingBBC;
  const isBlitzPay    = itemType === "blitzpay";
  const isIncoming    = itemType === "invoice-incoming";
  const isTransfer    = itemType === "transfer";
  const isWithdrawal  = itemType === "withdrawal";

  const accent = typeConfig.accent;

  const status        = (item.status || item.invoice_status || "pending").toLowerCase();
  const poNumber      = item.po_number || item.unique_code || `#${item.id}`;
  const date          = item.payout_date || item.created_at || item.date;
  const formattedDate = date ? new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const amount        = Number(item.amount || item.amount_due || item.net_amount || 0);
  const isCredit      = item.direction === "credit";
  const invoiceRoute  = getInvoiceRoute(item, itemType);
  const hasInvoicePage = invoiceRoute !== null;
  const isMulti       = item.isMultiRecipient && item.recipientCount > 1;
  const firstPayout   = item.payoutDetailsArray?.[0];
  const showExternalPO = isBlitzPay && !isNeedsApproval;

  const deriveFeePayer = () => {
    const explicit = item.fee_payer || item.custom_details?.fee_payer || item.feeBreakdown?.feePayer || item.payoutDetails?.fee_payer || firstPayout?.fee_payer;
    if (explicit) return explicit;
    const role = firstPayout?.role || item.payoutDetails?.role || item.role;
    if (role === "Client")  return "me";
    if (role === "Creator") return "creator";
    const rate = firstPayout?.interest_rate ?? item.payoutDetails?.interest_rate ?? item.interest_rate;
    if (rate != null) return Number(rate) === 0 ? "me" : "creator";
    return null;
  };
  const feePayer         = deriveFeePayer();
  const youPaidFee       = feePayer === "me";
  const isBlitzPayTiming = item.blitzpay || item.payment_timing === "instant";

  const handleViewFullInvoice = (e) => { e.stopPropagation(); if (invoiceRoute) navigate(invoiceRoute); };

  const creatorsReceiveAmount = (() => {
    if (!item.feeBreakdown?.isFunded) return 0;
    if (item.totalPayoutNetAmount > 0) return item.totalPayoutNetAmount;
    return item.feeBreakdown.invoiceAmount - item.feeBreakdown.platformFee;
  })();

  // ── Card chrome ─────────────────────────────────────────────────────── //
  const cardBorderColor = accent ? (isNeedsApproval ? accent.main : accent.border) : isNeedsApproval ? "warning.main" : "divider";
  const cardBorderLeft  = accent ? `4px solid ${isNeedsApproval ? accent.main : accent.border}` : undefined;
  const cardBg          = accent ? typeConfig.bgGradient() : typeConfig.bgGradient(theme);
  const amountColor     = isIncomingBBC ? YG_DEEP : isOutgoingBBC ? Y_DEEP : isBlitzPay ? R_DEEP : isIncoming ? G_DEEP : (isTransfer || isWithdrawal) ? (isCredit ? G_DEEP : R_DEEP) : "primary.main";
  const amountBoxBg     = accent ? alpha(accent.main, 0.06) : alpha(theme.palette.background.paper, 0.2);
  const amountBoxBorder = accent ? accent.border : "divider";

  // ── Type pill ───────────────────────────────────────────────────────── //
  const pillLabel = item.isMerged && isMulti ? `BlitzPay · ${item.recipientCount}` : item.isMerged ? "Invoice + Payout" : typeConfig.label;
  const pillBg    = typeConfig.pillBg  || theme.palette.grey[300];
  const pillInk   = typeConfig.pillInk || theme.palette.text.primary;
  const getPillIcon = () => {
    if (isIncomingBBC) return <CallSplitIcon />;
    if (isOutgoingBBC) return <BoltIcon />;
    if (isBlitzPay)    return <SwapHorizIcon />;
    if (isIncoming)    return <TrendingUpIcon />;
    if (isTransfer)    return <SwapHorizIcon />;
    if (isWithdrawal)  return <ArrowUpIcon />;
    return <ReceiptIcon />;
  };

  // ── Status element ───────────────────────────────────────────────────── //
  const statusEl = (() => {
    if (isNeedsApproval) return (
      <Chip
        label="Action Required" size="small"
        icon={<WarningIcon sx={{ fontSize: "13px !important", color: `${accent ? accent.deep : undefined} !important` }} />}
        sx={accent ? { fontWeight: 700, bgcolor: accent.tint, color: accent.deep, border: `1px solid ${accent.border}`, fontSize: "0.68rem", height: 22 } : { fontWeight: 600, height: 22 }}
        color={accent ? undefined : "warning"}
      />
    );
    if (isOutgoingBBC) return <StatusPill {...getBBCStatusPillProps(status, true)} />;
    if (isIncomingBBC) return <StatusPill {...getBBCStatusPillProps(status, false)} />;
    return getStatusChip(status);
  })();

  // ── Banner (BBC context / supplemental) ─────────────────────────────── //
  const contextBannerText = (() => {
    if (isIncomingBBC) {
      if (isNeedsApproval)       return "Your approval commits payment. Contractors see their shares immediately.";
      if (status === "approved") return "Approved — contractors can elect instant payout. Funds settle at payment term.";
                                 return "Blitz Split invoice from your vendor.";
    }
    if (isOutgoingBBC) {
      if (status === "pending")                    return "Waiting for client approval. Contractors see amounts once approved.";
      if (status === "approved")                   return "Client approved — contractors can now elect ⚡ instant payout.";
      if (status === "paid" || status === "funded") return "Fully funded. All contractors have been paid or settled.";
                                                   return "Manage your Split roster and payout elections.";
    }
    return null;
  })();

  const supplementalContent = (() => {
    if (isBlitzPay && !item.isMerged) return (
      <Alert severity="error" sx={{ py: 0.5, bgcolor: R_TINT, border: `1px solid ${R_BORDER}`, "& .MuiAlert-icon": { color: R } }}>
        <Typography variant="caption" sx={{ color: R_DEEP }}><strong>BlitzPay:</strong> Instant payment to creator(s)</Typography>
      </Alert>
    );
    if (item.isMerged) return (
      <Alert severity="success" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>{isMulti ? `BlitzPay · ${item.recipientCount} Creators` : "BlitzPay + Payout"}:</strong>{" "}
          {isMulti ? "Batch invoice + all creator payouts" : "Invoice and creator payout combined"}
          {feePayer && <> · <strong>{youPaidFee ? "You paid fee" : "Fee to vendors"}</strong></>}
        </Typography>
      </Alert>
    );
    if (isTransfer) return (
      <Alert severity="info" sx={{ py: 0.5, bgcolor: B_TINT, border: `1px solid ${B_BORDER}`, "& .MuiAlert-icon": { color: B } }}>
        <Typography variant="caption" sx={{ color: B_DEEP }}><strong>Internal Transfer:</strong> Balance adjustment</Typography>
      </Alert>
    );
    if (isWithdrawal) return (
      <Box sx={{ py: 0.75, px: 1.5, borderRadius: 1, bgcolor: B_TINT, border: `1px solid ${B_BORDER}` }}>
        <Typography variant="caption" sx={{ color: B_DEEP }}><strong>Withdrawal:</strong> Funds to {item.payment_service || "bank"}</Typography>
      </Box>
    );
    if (itemType === "payout") return (
      <Chip
        icon={feePayer ? (youPaidFee ? <CheckCircleIcon /> : <WarningIcon />) : (isBlitzPayTiming ? <SpeedIcon /> : <ScheduleIcon />)}
        label={feePayer ? (youPaidFee ? "You paid the fee" : "Fee to vendor") : (isBlitzPayTiming ? "Instant Payment" : "Net 30")}
        color={feePayer ? (youPaidFee ? "success" : "warning") : (isBlitzPayTiming ? "primary" : "default")}
        size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: "0.7rem" }}
      />
    );
    return null;
  })();

  // ── CTA ─────────────────────────────────────────────────────────────── //
  const openInNewBtn = hasInvoicePage ? (
    <Tooltip title="View Full Invoice">
      <IconButton onClick={handleViewFullInvoice}
        sx={{ border: "1px solid", borderRadius: 2, minWidth: 44, borderColor: accent ? accent.border : "primary.main", color: accent ? accent.deep : "primary.main", bgcolor: accent ? accent.tint : alpha(theme.palette.primary.main, 0.05), "&:hover": { bgcolor: accent ? alpha(accent.main, 0.15) : alpha(theme.palette.primary.main, 0.1) } }}>
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const ctaEl = (() => {
    if (isNeedsApproval && isIncomingBBC) return (
      <>
        <Button fullWidth variant="contained" size="medium" onClick={() => onApprove?.(item.id)} startIcon={<CallSplitIcon />}
          sx={{ borderRadius: 2, fontWeight: 800, bgcolor: YG, color: YG_INK, boxShadow: `0 4px 14px ${alpha(YG, 0.4)}`, "&:hover": { bgcolor: YG_DARK, transform: "translateY(-1px)" }, transition: "all 0.2s ease", textTransform: "none" }}>
          Review Split ⚡
        </Button>
        {hasInvoicePage && (
          <Tooltip title="View Full Invoice">
            <IconButton onClick={handleViewFullInvoice} sx={{ border: "2px solid", borderColor: YG_BORDER, borderRadius: 2, minWidth: 44, bgcolor: YG_TINT, "&:hover": { bgcolor: alpha(YG, 0.15) }, color: YG_DEEP }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
    if (isNeedsApproval && isIncoming) return (
      <>
        <Button fullWidth variant="contained" size="medium" onClick={() => onApprove?.(item.id)} startIcon={<CheckCircleIcon />}
          sx={{ borderRadius: 2, fontWeight: 700, bgcolor: G, color: G_INK, boxShadow: `0 4px 14px ${alpha(G, 0.35)}`, "&:hover": { bgcolor: G_DARK, transform: "translateY(-1px)" }, transition: "all 0.2s ease", textTransform: "none" }}>
          Review & Approve
        </Button>
        {hasInvoicePage && (
          <Tooltip title="View Full Invoice">
            <IconButton onClick={handleViewFullInvoice} sx={{ border: "2px solid", borderColor: G_BORDER, borderRadius: 2, minWidth: 44, bgcolor: G_TINT, "&:hover": { bgcolor: alpha(G, 0.15) }, color: G_DEEP }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
    if (isNeedsApproval) return (
      <>
        <Button fullWidth variant="contained" size="medium" onClick={() => onApprove?.(item.id)} startIcon={<CheckCircleIcon />}
          sx={{ borderRadius: 2, fontWeight: 600, textTransform: "none" }} color="warning">
          Review & Approve
        </Button>
        {hasInvoicePage && (
          <Tooltip title="View Full Invoice">
            <IconButton onClick={handleViewFullInvoice} sx={{ border: "2px solid", borderColor: "warning.main", borderRadius: 2, minWidth: 44, bgcolor: alpha(theme.palette.warning.main, 0.1), "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.2) }, color: "warning.main" }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
    if (isNeedsManage) return (
      <Button fullWidth variant="contained" size="medium" onClick={() => onApprove?.(item.id)} startIcon={<SettingsIcon />}
        sx={{ borderRadius: 2, fontWeight: 800, bgcolor: Y, color: Y_INK, boxShadow: `0 4px 14px ${alpha(Y, 0.35)}`, "&:hover": { bgcolor: Y_DARK, transform: "translateY(-1px)" }, transition: "all 0.2s ease", textTransform: "none" }}>
        Manage Split ⚡
      </Button>
    );
    return (
      <>
        <Button fullWidth variant="outlined" size="medium" onClick={() => onViewDetails?.(item)}
          startIcon={completed || isTransfer || isWithdrawal ? <InfoIcon /> : <VisibilityIcon />}
          sx={{ borderRadius: 2, textTransform: "none", ...(accent && { borderColor: accent.border, color: accent.deep, "&:hover": { borderColor: accent.main, bgcolor: accent.tint } }) }}>
          View Details
        </Button>
        {openInNewBtn}
        {canEdit && (
          <Tooltip title="Edit Invoice">
            <IconButton onClick={() => onEdit?.(item)} color="secondary"
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, minWidth: 44, "&:hover": { borderColor: "secondary.main", bgcolor: alpha(theme.palette.secondary.main, 0.05) } }}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  })();

  // ─────────────────────────────────────────────────────────────────────── //
  // RENDER
  // Uniform skeleton — every card has identical sections with fixed heights.
  // ─────────────────────────────────────────────────────────────────────── //
  return (
    <Card sx={{
      display: "flex",
      flexDirection: "column",
      border: "2px solid",
      borderColor: cardBorderColor,
      ...(cardBorderLeft && { borderLeft: cardBorderLeft }),
      background: cardBg,
      transition: "all 0.3s ease",
      overflow: "hidden",
      // height is driven by content — grid/flex container should equalize;
      // each section slot has a fixed minHeight so cards naturally align
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: (isNeedsApproval || isNeedsManage) ? 6 : 4,
        borderColor: accent ? accent.dark : isNeedsApproval ? "warning.dark" : `${typeConfig.color}.main`,
      },
    }}>
      <CardContent sx={{ p: 2, pb: "10px !important", flex: 1, display: "flex", flexDirection: "column" }}>

        {/* ── Section A: Header — 56px ───────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, gap: 1, minHeight: 48, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, overflow: "hidden" }}>
            <Avatar sx={{ width: 40, height: 40, flexShrink: 0, boxShadow: 2, bgcolor: accent ? accent.main : `${typeConfig.color}.main`, color: accent ? accent.ink : "white" }}>
              {typeConfig.mkIcon()}
            </Avatar>
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <TypePill bg={pillBg} ink={pillInk} icon={getPillIcon()} label={pillLabel} />
              <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: "0.68rem", mt: "2px" }}>
                {formattedDate}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flexShrink: 0 }}>{statusEl}</Box>
        </Box>

        {/* ── Section B: Entity — 3 fixed-height lines (72px) ────────── */}
        <Box sx={{ mb: 1.25, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap title={entityInfo.name}
            sx={{ lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis" }}>
            {entityInfo.name}
          </Typography>
          {/* Always rendered — height reserved even when empty */}
          <Typography variant="caption" color="text.secondary" noWrap
            sx={{ display: "block", minHeight: "1.25em", overflow: "hidden", textOverflow: "ellipsis", mt: "2px", fontSize: "0.72rem" }}>
            {entityInfo.subtitle || "\u00A0"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap
            sx={{ display: "block", fontSize: "0.67rem", mt: "2px", overflow: "hidden", textOverflow: "ellipsis" }}>
            {isTransfer ? "Txn:" : isWithdrawal ? "ID:" : "PO:"}{" "}
            {(poNumber || "").slice(0, 26)}{(poNumber || "").length > 26 ? "…" : ""}
          </Typography>
        </Box>

        {/* ── Section C: Meta chips — 26px always present ────────────── */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minHeight: 26, mb: 1.25, alignItems: "center" }}>
          {isAnyBBC && item.payment_term && (
            <Chip label={`Net ${item.payment_term}`} size="small" variant="outlined"
              sx={{ height: 20, fontSize: "0.63rem", fontWeight: 700, borderColor: isIncomingBBC ? YG_BORDER : Y_BORDER, color: isIncomingBBC ? YG_DEEP : Y_DEEP }} />
          )}
          {isTransfer && (
            <Chip label={isCredit ? "Credit" : "Debit"} size="small" color={isCredit ? "success" : "error"} variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />
          )}
          {item.isMerged && feePayer && (
            <Chip
              icon={youPaidFee ? <CheckCircleIcon sx={{ fontSize: "11px !important" }} /> : <WarningIcon sx={{ fontSize: "11px !important" }} />}
              label={youPaidFee ? "You paid fee" : "Fee to vendors"}
              size="small" color={youPaidFee ? "success" : "warning"} variant="outlined"
              sx={{ height: 20, fontSize: "0.63rem" }}
            />
          )}
          {showExternalPO && (
            <ExternalPOSection
              externalPoNumber={item.external_po_number}
              erpSystemName={item.erp_system || item.vendor_system_name}
              onUpdate={() => onViewDetails?.(item)}
            />
          )}
        </Box>

        {/* ── Section D: Amount box — fixed ~64px ────────────────────── */}
        <Box sx={{ bgcolor: amountBoxBg, px: 2, py: 1.5, borderRadius: 2, mb: 1.5, border: "1px solid", borderColor: amountBoxBorder, minHeight: 56 }}>
          {item.feeBreakdown?.isFunded ? (
            <Stack spacing={0.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} noWrap>Total Debited</Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main" sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                  {currency.format(item.feeBreakdown.invoiceAmount)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button size="small" onClick={() => setBreakdownExpanded(!breakdownExpanded)}
                  endIcon={breakdownExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: "none", fontSize: "0.67rem", color: "text.secondary", minHeight: 20, py: 0 }}>
                  {breakdownExpanded ? "Hide" : "Show"} breakdown
                </Button>
              </Box>
              <Collapse in={breakdownExpanded}>
                <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" noWrap>{isMulti ? `Creators (${item.recipientCount})` : "Creator receives"}</Typography>
                    <Typography variant="caption" fontWeight={600} color="success.main" sx={{ flexShrink: 0 }}>{currency.format(creatorsReceiveAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" color="text.secondary" noWrap>Platform Fee ({item.feeBreakdown.feePercentage}%)</Typography>
                    <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ flexShrink: 0 }}>+{currency.format(item.feeBreakdown.platformFee)}</Typography>
                  </Box>
                  {feePayer && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" noWrap>Fee paid by</Typography>
                      <Chip size="small" label={youPaidFee ? "You" : "Vendors"} color={youPaidFee ? "success" : "warning"} variant="outlined" sx={{ height: 18, fontSize: "0.63rem", fontWeight: 700, flexShrink: 0 }} />
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="caption" fontWeight={700} noWrap>Total Debited</Typography>
                    <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ flexShrink: 0 }}>{currency.format(item.feeBreakdown.invoiceAmount)}</Typography>
                  </Box>
                  <Alert severity="info" sx={{ mt: 0.5, py: 0.25 }}>
                    <Typography variant="caption">Charged to spending power</Typography>
                  </Alert>
                </Stack>
              </Collapse>
            </Stack>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, minHeight: 36 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                {isIncomingBBC ? "Invoice total" : isOutgoingBBC ? "Split amount" : isMulti ? `Total (${item.recipientCount})` : "Amount"}
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ color: amountColor, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                {(isTransfer || isWithdrawal) && (isCredit ? "+" : "-")}
                {currency.format(amount)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Section E: Banner slot — fixed 52px, always rendered ────── */}
        <Box sx={{ minHeight: 52, flex: 1, display: "flex", alignItems: "flex-start" }}>
          {isAnyBBC && contextBannerText ? (
            <Box sx={{ width: "100%", p: 1.25, borderRadius: 1.5, bgcolor: isIncomingBBC ? YG_TINT : Y_TINT, border: `1px solid ${isIncomingBBC ? YG_BORDER : Y_BORDER}` }}>
              <Typography variant="caption" sx={{ color: isIncomingBBC ? YG_DEEP : Y_DEEP, fontWeight: 700, lineHeight: 1.55, display: "block" }}>
                {isIncomingBBC
                  ? <CallSplitIcon sx={{ fontSize: 11, verticalAlign: "middle", mr: 0.5 }} />
                  : <SettingsIcon  sx={{ fontSize: 11, verticalAlign: "middle", mr: 0.5 }} />}
                {contextBannerText}
              </Typography>
            </Box>
          ) : supplementalContent ? (
            <Box sx={{ width: "100%" }}>{supplementalContent}</Box>
          ) : (
            // Empty spacer — preserves height parity across types
            <Box sx={{ width: "100%", height: 52 }} />
          )}
        </Box>

      </CardContent>

      {/* ── Section F: Actions — fixed 64px ─────────────────────────── */}
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          {ctaEl}
        </Stack>
      </CardActions>
    </Card>
  );
};

export default UnifiedActivityCard;